/**
 * 거래처(제휴업체) 목록 페이지
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, Badge, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import DataTable, { Column } from '../../components/common/DataTable';
import Pagination from '../../components/common/Pagination';
import FilterBar, { FilterField } from '../../components/common/FilterBar';
import { partnerService } from '../../services/partnerService';
import { Partner, PartnerFilter } from '../../types/partner';

const PartnersList: React.FC = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<PartnerFilter>({
    page: 1,
    limit: 20,
  });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['partners', filter],
    queryFn: () => partnerService.getPartners(filter),
  });

  const getStatusBadge = (status: string) => {
    return status === 'active' ? (
      <Badge bg="success">활성</Badge>
    ) : (
      <Badge bg="secondary">비활성</Badge>
    );
  };

  const columns: Column<Partner>[] = [
    {
      key: 'id',
      label: 'ID',
      width: '80px',
    },
    {
      key: 'partner_name',
      label: '거래처명',
      render: (partner) => (
        <span
          style={{ cursor: 'pointer', color: '#00BCD4' }}
          onClick={() => navigate(`/admin/partners/${partner.id}`)}
        >
          {partner.partner_name}
        </span>
      ),
    },
    {
      key: 'partner_code',
      label: '거래처 코드',
      render: (partner) => (
        <span style={{ color: '#ccc' }}>{partner.partner_code}</span>
      ),
    },
    {
      key: 'api_key',
      label: 'API Key',
      render: (partner) => (
        <span style={{ color: '#ccc' }}>
          {partner.api_key ? (
            <Badge bg="success">있음</Badge>
          ) : (
            <Badge bg="secondary">없음</Badge>
          )}
        </span>
      ),
    },
    {
      key: 'contact_name',
      label: '담당자',
      render: (partner) => (
        <span style={{ color: '#ccc' }}>{partner.contact_name || '-'}</span>
      ),
    },
    {
      key: 'email',
      label: '이메일',
      render: (partner) => (
        <span style={{ color: '#ccc' }}>{partner.email || '-'}</span>
      ),
    },
    {
      key: 'phone',
      label: '연락처',
      render: (partner) => (
        <span style={{ color: '#ccc' }}>{partner.phone || '-'}</span>
      ),
    },
    {
      key: 'commission_rate',
      label: '수수료율',
      render: (partner) => (
        <span style={{ color: '#00BCD4', fontWeight: 'bold' }}>
          {partner.commission_rate}%
        </span>
      ),
    },
    {
      key: 'status',
      label: '상태',
      render: (partner) => getStatusBadge(partner.status),
    },
  ];

  const filterFields: FilterField[] = [
    {
      name: 'search',
      label: '검색',
      type: 'text',
      placeholder: '거래처명, 코드로 검색',
    },
    {
      name: 'status',
      label: '상태',
      type: 'select',
      options: [
        { value: '', label: '전체' },
        { value: 'active', label: '활성' },
        { value: 'inactive', label: '비활성' },
      ],
    },
  ];

  const handleFilterChange = (values: Record<string, any>) => {
    setFilter((prev) => ({
      ...prev,
      ...values,
      page: 1,
    }));
  };

  const handlePageChange = (page: number) => {
    setFilter((prev) => ({ ...prev, page }));
  };

  const handleRowClick = (partner: Partner) => {
    navigate(`/admin/partners/${partner.id}`);
  };

  return (
    <AdminLayout>
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 style={{ color: '#fff' }}>거래처 관리</h1>
          <Button variant="primary" onClick={() => navigate('/admin/partners/new')}>
            거래처 등록
          </Button>
        </div>

        <Card className="mb-4">
          <Card.Body>
            <FilterBar
              fields={filterFields}
              values={filter}
              onChange={handleFilterChange}
              onSearch={() => refetch()}
              onReset={() => setFilter({ page: 1, limit: 20 })}
            />
          </Card.Body>
        </Card>

        <Card>
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                총 <strong>{data?.pagination.total_count || 0}</strong>건
              </div>
            </div>

            <DataTable
              columns={columns}
              data={data?.partners || []}
              loading={isLoading}
              emptyMessage="거래처가 없습니다."
              onRowClick={handleRowClick}
            />

            {data && data.pagination.total_pages > 1 && (
              <Pagination
                currentPage={data.pagination.current_page}
                totalPages={data.pagination.total_pages}
                onPageChange={handlePageChange}
              />
            )}
          </Card.Body>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default PartnersList;

